import React from 'react';
import { Calendar, User, Building2 } from 'lucide-react';
import { AdminBookingListItem } from '../types';

interface AdminBookingTableProps {
  bookings: AdminBookingListItem[];
  loading: boolean;
  onViewDetails: (bookingId: number) => void;
}

export const AdminBookingTable: React.FC<AdminBookingTableProps> = ({
  bookings,
  loading,
  onViewDetails,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
        <p className="text-gray-500">
          No bookings match your current filters.
        </p>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              ID
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              User
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Provider
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">
              Service Type
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Appointment
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
              Created
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {bookings.map((booking) => (
            <tr
              key={booking.bookingId}
              className="hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onViewDetails(booking.bookingId)}
            >
              <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                #{booking.bookingId}
              </td>
              <td className="px-3 py-3">
                <div className="flex items-center">
                  <User className="h-4 w-4 text-gray-400 mr-1.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {booking.userName || 'Unknown User'}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-3 py-3">
                <div className="flex items-center">
                  <Building2 className="h-4 w-4 text-gray-400 mr-1.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {booking.providerName || 'Unknown Provider'}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-3 py-3 text-sm text-gray-900 hidden xl:table-cell">
                {booking.serviceType || 'N/A'}
              </td>
              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                {formatDate(booking.appointmentDate)}
              </td>
              <td className="px-3 py-3 whitespace-nowrap">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  booking.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                  booking.status === 'completed' ? 'bg-green-100 text-green-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                </span>
              </td>
              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                {formatDate(booking.createdAt)}
              </td>
              <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails(booking.bookingId);
                  }}
                  className="text-blue-600 hover:text-blue-900"
                >
                  View Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
